import React from "react";
import {
  Button,
  CopyIcon,
  Modal,
  useToast,
} from "../../../components/ui";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import { useClient } from "../../clients/hooks/useClient";
import {
  appointmentState,
  appointmentTimeRange,
  attendeeName,
  clientIdOf,
  dayFull,
  durationLabel,
  initialsOf,
  isUpcoming,
  linkLabel,
} from "../appointments";
import { agendaLabel } from "../meetingAgenda";
import type { ClientAppointment } from "../types/appointments.types";
import { useAppointmentDetail } from "../hooks/useAppointmentDetail";

/** One labelled line of the details card. */
const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 px-4 py-2.5 3xl:px-5">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}
    </span>
    <span className="min-w-0 text-right text-[13px] 3xl:text-[14px] text-ink">
      {children}
    </span>
  </div>
);

export interface MeetingModalProps {
  /**
   * The row the dialog was opened on. It is what the dialog draws until the
   * read by `public_id` lands — see `useAppointmentDetail` — so the panel is
   * never blank and never waits.
   */
  appointment: ClientAppointment;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  onClose: () => void;
  /** Opens the reschedule dialog on the appointment as it now stands. */
  onReschedule: (appointment: ClientAppointment) => void;
  /** Opens the cancel dialog on it. */
  onCancel: (appointment: ClientAppointment) => void;
}

/**
 * One appointment, whole: who it is with, when it runs, where it happens, and
 * the three things that can still be done about it.
 *
 * Opened from a row of My task's timeline or from a row of the client's own
 * Appointment tab — one dialog for both, because "one appointment, read
 * whole" is the same question on either screen and a second copy of it is two
 * places for the Join to start disagreeing about when it should be offered.
 * It lived in `myTask` while that was the only screen with a row to press.
 *
 * Read from the server rather than from the row it was opened on — `GET /api/v1/appointments/{public_id}/`. The row is
 * drawn meanwhile, so nothing waits; what the read buys is that the Join, the
 * Reschedule and the Cancel underneath are offered against the record as it
 * stands now rather than as the list last saw it, which on a screen somebody
 * leaves open all morning is not the same thing.
 *
 * The client's address and number are the one thing the appointment record
 * does not carry — it names the client and gives their `public_id`, and
 * nothing more — so they are read off the client. Absent while that read is
 * out, and absent for good where it fails: a dialog about an appointment is
 * not the place to report that a client record could not be fetched, and the
 * name is already on the row.
 *
 * What the footer offers turns on whether the appointment is still live. One
 * that has been completed, called off, or has run its course with nobody
 * marking it is a record to read: there is nothing to join, moving it would be
 * rewriting history, and calling it off is either done or no longer the right
 * word. Those get a way out and nothing else.
 */
const MeetingModal: React.FC<MeetingModalProps> = ({
  appointment: opened,
  gate,
  onClose,
  onReschedule,
  onCancel,
}) => {
  const toast = useToast();
  const { appointment } = useAppointmentDetail(opened);

  // Read once, when the dialog opens: a "now" that moved underneath the footer
  // would take the Join away from under somebody's pointer.
  const [now] = React.useState(() => new Date());
  // What the record says, with the clock folded in — the same five words the
  // client's table reads. The dialog asks it two questions: whether there is
  // still anything to do about the appointment, and which of the two notes a
  // settled one has to show.
  //
  // `isUpcoming` rather than a reading of `state`, because "still standing and
  // not yet over" is one question the feature already answers in one place.
  // Asked of the state there would be the clock's "Awaiting" to remember not
  // to count, which is exactly the mistake that helper exists to prevent.
  const state = appointmentState(appointment, now);
  const live = isUpcoming(appointment, now);

  // Whoever it is with. The appointment names them; this is what adds the way
  // to reach them. Nothing is asked for where the row carries no client id,
  // which is every row off a client's own screen — and every row booked
  // against an enquiry, which has no client record to reach for. An enquiry
  // has contact details of its own, but they are the enquiry's rather than
  // this dialog's to fetch, and the name it is booked under is on the row.
  const { client } = useClient(clientIdOf(appointment) ?? "");

  const who = attendeeName(appointment);
  const name = who || client?.firstName || "Unnamed client";
  const online = appointment.type === "Online";
  const joinUrl = live && online ? appointment.meetingUrl : null;
  const starts = new Date(appointment.startsAt);

  const copyLink = async () => {
    if (!appointment.meetingUrl) return;

    try {
      await navigator.clipboard.writeText(appointment.meetingUrl);
      toast.success("Meeting link copied", "Paste it wherever it is needed.");
    } catch (cause) {
      // Clipboard access is refused outright in some browsers and over plain
      // HTTP. The link is on screen either way, so this says so rather than
      // failing silently at somebody who is watching for a confirmation.
      console.error("could not copy the meeting link:", cause);
      toast.error(
        "Could not copy the link",
        "Your browser would not allow it — select the link and copy it by hand."
      );
    }
  };

  return (
    <Modal
      open
      title="Meeting"
      onClose={onClose}
      // The body is two bordered cards. A rule under the heading would land a
      // few pixels above the first card's own top edge and read as one thick
      // line that is not quite straight; the space alone separates them.
      hideTitleRule
      // The buttons do want one. They are not the end of the detail being
      // read — they are what to do about it, and without a rule the Join
      // reads as another row of the card above it.
      footerRule
      // The description can run to five hundred characters, which on a laptop
      // is a panel taller than the screen. Scrolling the body rather than the
      // panel keeps the Join and the Reschedule where they were put, instead
      // of a screenful below the last line somebody read.
      scrollBody
      className="max-w-[500px] max-h-[88vh]"
      footer={
        live ? (
          <>
            {/* Set apart from the other two, and the only destructive thing
                here. Quiet rather than filled: calling an appointment off is a
                real answer but it is not what somebody opened this to do. */}
            <Button
              variant="dangerQuiet"
              size="md"
              className="mr-auto"
              onClick={() => onCancel(appointment)}
              {...gate}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => onReschedule(appointment)}
              {...gate}
            >
              Reschedule
            </Button>
            {/* A link dressed as the primary button: it goes somewhere, so it
                opens in a new tab on a middle click. Only ever drawn for an
                online appointment that has a link on it — an in-person sitting
                has nothing to press, and the address is in the card above. */}
            {joinUrl && (
              <a
                href={joinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-[6px] bg-brand px-5 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                Join
              </a>
            )}
          </>
        ) : (
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {/* Who it is with, and how to reach them — the first thing anybody
            opening an appointment wants, and the thing the row they pressed
            had least room for. */}
        <section className="flex items-center gap-3 rounded-[6px] bg-[#F4FBFC] px-4 py-3.5">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#DCF3F6] text-[13px] font-semibold text-[#00707E]"
          >
            {initialsOf(who || "")}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[14px] 3xl:text-[15px] font-medium text-ink">
              {name}
            </p>
            {/* Whatever the client record gave up. A line with nothing on it
                is left off rather than drawn as two dashes: the dialog is
                about the appointment, and an empty contact line reads as
                something that failed to load. */}
            {(client?.email || client?.phone) && (
              <p className="flex flex-wrap items-center gap-x-2 text-[12px] 3xl:text-[13px] text-ink-muted">
                {client?.email && (
                  <span className="truncate">{client.email}</span>
                )}
                {client?.email && client?.phone && (
                  <span aria-hidden="true" className="text-ink-subtle">
                    •
                  </span>
                )}
                {client?.phone && <span>{client.phone}</span>}
              </p>
            )}
          </div>
        </section>

        <section className="rounded-[6px] border border-line">
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 3xl:px-5">
            <h3 className="text-[14px] 3xl:text-[15px] font-medium text-ink">
              Meeting details
            </h3>
            <span
              className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                online
                  ? "bg-[#E6FAFB] text-[#007E8C]"
                  : "bg-[#F1F3F5] text-[#62636C]"
              }`}
            >
              {appointment.type}
            </span>
          </div>

          <div className="divide-y divide-line">
            <DetailRow label="Date">
              {Number.isNaN(starts.getTime()) ? "-" : dayFull(starts)}
            </DetailRow>

            {/* Both ends and the length between them. The only place in the
                app that says when an appointment stops — which is what the
                dialog is being read against a clock to find out. */}
            <DetailRow label="Time">
              <span className="whitespace-nowrap">
                {appointmentTimeRange(appointment)}
              </span>
              {appointment.durationMinutes > 0 && (
                <>
                  <span aria-hidden="true" className="px-2 text-ink-subtle">
                    •
                  </span>
                  <span className="whitespace-nowrap text-ink-muted">
                    {durationLabel(appointment.durationMinutes)}
                  </span>
                </>
              )}
            </DetailRow>

            {/* What the session is for. Under the clock and over the way in,
                which is the order somebody reads the card in: when, what, and
                then how to get there. A dash where none was chosen — the
                booking dialog asks but does not insist — rather than a row
                left out, for the same reason the link row is never left out. */}
            <DetailRow label="Agenda">{agendaLabel(appointment.agenda) || "-"}</DetailRow>

            {/* An online appointment has a link, an in-person one has an
                address, and either can be booked without one. A dash where
                there is none, rather than a row left out — a details card that
                changes shape row by row is one nobody can scan. */}
            {online ? (
              <DetailRow label="Meeting link">
                {appointment.meetingUrl ? (
                  <span className="inline-flex max-w-full items-center gap-1.5">
                    <a
                      href={appointment.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate font-medium text-[#00A5B4] transition-colors hover:text-[#007E8C] hover:underline"
                    >
                      {linkLabel(appointment.meetingUrl)}
                    </a>
                    <button
                      type="button"
                      onClick={copyLink}
                      aria-label="Copy the meeting link"
                      className="flex-shrink-0 text-ink-muted transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                    >
                      <CopyIcon className="h-4 w-4" />
                    </button>
                  </span>
                ) : (
                  "-"
                )}
              </DetailRow>
            ) : (
              <DetailRow label="Address">{appointment.address || "-"}</DetailRow>
            )}

            {/* Only on one that was called off, and only where somebody said
                why. It is the whole of what a cancelled record has left to
                tell anybody. */}
            {state === "Cancelled" && appointment.cancelReason && (
              <DetailRow label="Reason">{appointment.cancelReason}</DetailRow>
            )}

            {/* And its opposite number: what the practitioner wrote down when
                they said the session took place. A row only ever has one of
                the two — an appointment that was completed was never called
                off — so the card never grows both. */}
            {state === "Completed" && appointment.completionNote && (
              <DetailRow label="Note">{appointment.completionNote}</DetailRow>
            )}
          </div>
        </section>

        {/* Outside the card, and only where there is something to read.
            Outside because it is prose and the card is a table of facts: a row
            of label-and-value with a paragraph in the value column reads as a
            cell that has broken its own layout. Left off entirely when empty,
            which is the opposite rule to the rows above — those are a shape
            somebody scans and a missing one is a hole in it, where this is a
            block of writing and an empty one is a heading over nothing. */}
        {!!appointment.description && (
          <section>
            <h3 className="mb-1.5 text-[14px] 3xl:text-[15px] font-medium text-ink">
              Description
            </h3>
            {/* `whitespace-pre-line`, so the paragraph breaks the writer put
                in survive. It is typed into a textarea, and prose that came
                back as one unbroken block would lose the only structure it
                was given. */}
            <p className="whitespace-pre-line text-[13px] 3xl:text-[14px] leading-[1.6] text-ink-muted">
              {appointment.description}
            </p>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default MeetingModal;
